import { Alert } from '@mui/joy';
import {
  Workspace,
  WorkspaceHeader,
  SummaryCards,
  StudioModal as Modal,
  StudioDialog as ModalDialog,
  money,
} from '../../components/Workspace/Workspace';
import { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box,
  Sheet,
  Stack,
  Typography,
  Button,
  Input,
  Select,
  Option,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Chip,
  Avatar,
  ModalClose,
  List,
  ListItemButton,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';

import { LayoutContext } from '../../context';
import { APIGetQualityGoals, APIGetQualityMastersListForGoals } from '../../api';
import ModalQualityGoals from '../../components/Modals/ModalQualityGoals';

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const pctHex = (p) => (p >= 80 ? '#16a34a' : p >= 50 ? '#f59e0b' : '#dc2626');
const fmtMoney = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₽`;
const initialsOf = (m) =>
  (
    (m?.name || m?.master_name || '?').charAt(0) + (m?.last_name || m?.master_last_name || '').charAt(0)
  ).toUpperCase();

// ── Мини-метрика ─────────────────────────────────────────
function Mini({ icon, label, value }) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center', px: 0.5 }}>
      <Box sx={{ color: 'text.tertiary', display: 'flex', justifyContent: 'center', mb: 0.25 }}>{icon}</Box>
      <Typography level="title-sm" sx={{ fontWeight: 700, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
        {label}
      </Typography>
    </Box>
  );
}

// ── Карточка цели ────────────────────────────────────────
function QualityGoalCard({ goal, onOpen }) {
  const p = goal.progress || {},
    bc = goal.bonus_calculation || {};
  const kpi = Number(bc.kpi_percent ?? goal.overall_percent ?? 0);
  const open = () => onOpen(goal);
  const metrics = [
    {
      label: 'В срок',
      value: p.deadline_compliance,
      target: goal.target_deadline_compliance,
      unit: '%',
      lower: false,
      icon: <VerifiedRoundedIcon />,
    },
    {
      label: 'Конверсия',
      value: p.funnel_conversion,
      target: goal.target_funnel_conversion,
      unit: '%',
      lower: false,
      icon: <SpeedRoundedIcon />,
    },
    {
      label: 'Переделки',
      value: p.rework_rate,
      target: goal.target_rework_rate,
      unit: '%',
      lower: true,
      icon: <ReplayRoundedIcon />,
    },
    {
      label: 'Средний цикл',
      value: p.avg_cycle_time_days,
      target: goal.target_avg_cycle_time_days,
      unit: ' дн.',
      lower: true,
      icon: <TimerRoundedIcon />,
    },
  ];
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Box
        className="studio-goal-card studio-card-button"
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
      >
        <Box className="studio-goal-body">
          <Stack direction="row" gap={1.25} alignItems="center">
            <Avatar variant="soft" color="primary">
              {initialsOf(goal)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography level="title-md">
                {goal.master_last_name} {goal.master_name}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                Мастер по качеству
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2.5, mb: 1.5 }}
          >
            <Typography level="body-sm">Общий KPI</Typography>
            <Chip size="sm" variant="soft" color={kpi >= 80 ? 'success' : 'warning'}>
              {kpi >= 80 ? 'В плане' : 'Требует внимания'}
            </Chip>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography level="h3" component="p">
              {Math.round(kpi)}%
            </Typography>
            <LinearProgress
              determinate
              value={Math.max(0, Math.min(100, kpi))}
              color={kpi >= 80 ? 'success' : 'warning'}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Box sx={{ mt: 1.5 }}>
            {metrics.map((m) => (
              <Box key={m.label} className="studio-metric-row">
                <Stack direction="row" gap={1} alignItems="center">
                  <Box sx={{ color: 'primary.plainColor', display: 'flex' }}>{m.icon}</Box>
                  <Box>
                    <Typography level="body-sm">{m.label}</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {m.lower ? 'Чем меньше, тем лучше' : 'Чем выше, тем лучше'}
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography level="title-sm">
                    {m.value == null
                      ? '—'
                      : `${Number(m.value).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}${m.unit}`}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {Number(m.target) > 0
                      ? `Цель ${m.lower ? '≤' : '≥'} ${m.target}${m.unit}`
                      : 'Цель не задана'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="studio-goal-bottom">
          <Box>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Расчётная выплата
            </Typography>
            <Typography level="title-lg" color="primary">
              {money(bc.total_salary)}
            </Typography>
          </Box>
          <Typography level="body-sm" color="primary">
            Подробнее →
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Модалка выбора мастера ───────────────────────────────
function AddMasterModal({ open, onClose, year, month, token, onPicked }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await APIGetQualityMastersListForGoals(token, { year, month });
      setList(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [token, year, month]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const available = list.filter((m) => !m.has_goal);

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10201 }}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 460 }, borderRadius: 'xl' }}>
        <ModalClose />
        <Typography level="title-md" sx={{ fontWeight: 700, mb: 1 }}>
          Добавить мастера — {MONTHS[month - 1]} {year}
        </Typography>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress />
          </Stack>
        ) : available.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 2 }}>
            Все мастера по качеству уже имеют цель на этот месяц (или их нет в системе).
          </Typography>
        ) : (
          <List sx={{ gap: 0.5, maxHeight: 360, overflow: 'auto' }}>
            {available.map((m) => (
              <ListItemButton key={m.id} onClick={() => onPicked(m)} sx={{ borderRadius: 'lg' }}>
                <Avatar variant="soft" color="primary" size="sm" sx={{ mr: 1 }}>
                  {((m.name || '?').charAt(0) + (m.last_name || '').charAt(0)).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                    {m.last_name} {m.name}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </ModalDialog>
    </Modal>
  );
}

export default function QualityGoalsPage() {
  const { tokenEmployee, account, effectiveRole } = useContext(LayoutContext);

  const isHead = useMemo(
    () => effectiveRole === 'head_manager' || account?.role === 'administrator',
    [effectiveRole, account?.role],
  );

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState([]);
  const [loadError, setLoadError] = useState('');
  const loadId = useRef(0);
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [startEdit, setStartEdit] = useState(false);

  const load = useCallback(async () => {
    if (!tokenEmployee) return;
    const id = ++loadId.current;
    setLoading(true);
    setLoadError('');
    try {
      const params = isHead ? { year, month } : { quality_master_id: account?.id, year, month };
      const data = await APIGetQualityGoals(tokenEmployee, { ...params, throwOnError: true });
      if (id !== loadId.current) return;
      setGoals(Array.isArray(data) ? data : []);
    } catch (e) {
      if (id === loadId.current) {
        setGoals([]);
        setLoadError('Не удалось загрузить цели качества. Повторите запрос.');
      }
    } finally {
      if (id === loadId.current) setLoading(false);
    }
  }, [tokenEmployee, isHead, account?.id, year, month]);

  useEffect(() => {
    load();
    return () => {
      ++loadId.current;
    };
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return goals;
    const q = search.toLowerCase();
    return goals.filter((g) => `${g.master_name} ${g.master_last_name}`.toLowerCase().includes(q));
  }, [goals, search]);

  const openGoal = (goal) => {
    setSelectedMaster({ id: goal.quality_master, name: goal.master_name, last_name: goal.master_last_name });
    setSelectedGoal(goal);
    setStartEdit(false);
    setModalOpen(true);
  };

  const onPicked = (m) => {
    setAddOpen(false);
    setSelectedMaster(m);
    setSelectedGoal(null);
    setStartEdit(true);
    setModalOpen(true);
  };

  const onSaved = () => {
    setModalOpen(false);
    load();
  };

  // ── Личный вид мастера по качеству ──
  if (!isHead) {
    const my = goals[0] || null;
    return (
      <Workspace>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography level="title-lg" sx={{ fontWeight: 700, mr: 'auto' }}>
            Моя цель качества
          </Typography>
          <Select
            value={month}
            onChange={(_, v) => setMonth(v)}
            startDecorator={<CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 'lg' }}
          >
            {MONTHS.map((m, i) => (
              <Option key={i + 1} value={i + 1}>
                {m}
              </Option>
            ))}
          </Select>
          <Select value={year} onChange={(_, v) => setYear(v)} sx={{ minWidth: 100, borderRadius: 'lg' }}>
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <Option key={y} value={y}>
                {y}
              </Option>
            ))}
          </Select>
        </Stack>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size="lg" />
          </Stack>
        ) : my ? (
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={4}>
              <QualityGoalCard goal={my} onOpen={openGoal} />
            </Grid>
          </Grid>
        ) : (
          <Sheet
            variant="soft"
            sx={{ borderRadius: 'xl', p: 4, textAlign: 'center', color: 'text.tertiary' }}
          >
            <VerifiedRoundedIcon sx={{ fontSize: 48, opacity: 0.4 }} />
            <Typography level="title-md" sx={{ mt: 1 }}>
              На {MONTHS[month - 1]} {year} цель ещё не назначена
            </Typography>
          </Sheet>
        )}
        <ModalQualityGoals
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          master={selectedMaster}
          initialGoal={selectedGoal}
          year={year}
          month={month}
          onSaved={onSaved}
          startInEdit={false}
          canEdit={false}
        />
      </Workspace>
    );
  }

  // ── Дашборд руководителя ──
  const average = (key) => {
    const values = goals.map((g) => g.progress?.[key]).filter((v) => v != null && Number.isFinite(Number(v)));
    return values.length
      ? `${Math.round(values.reduce((sum, v) => sum + Number(v), 0) / values.length)}%`
      : '—';
  };
  return (
    <Workspace>
      <WorkspaceHeader title="Цели качества" subtitle="Сроки, качество работ и результаты команды">
        <Button
          variant="outlined"
          color="neutral"
          onClick={load}
          loading={loading}
          startDecorator={<RefreshRoundedIcon />}
        >
          Обновить
        </Button>
        <Button onClick={() => setAddOpen(true)} startDecorator={<AddRoundedIcon />}>
          Добавить мастера
        </Button>
      </WorkspaceHeader>
      {loadError && (
        <Alert
          color="danger"
          endDecorator={
            <Button variant="plain" onClick={load}>
              Повторить
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      <SummaryCards
        items={[
          { label: 'Мастеров с целями', value: goals.length, icon: <VerifiedRoundedIcon /> },
          {
            label: 'Средний KPI',
            value: goals.length
              ? `${Math.round(goals.reduce((sum, g) => sum + Number(g.bonus_calculation?.kpi_percent ?? g.overall_percent ?? 0), 0) / goals.length)}%`
              : '—',
            icon: <SpeedRoundedIcon />,
          },
          {
            label: 'В срок',
            value: average('deadline_compliance'),
            hint: 'В среднем по мастерам',
            icon: <TimerRoundedIcon />,
            tone: 'success',
          },
          {
            label: 'Переделки',
            value: average('rework_rate'),
            hint: 'В среднем по мастерам',
            icon: <ReplayRoundedIcon />,
            tone: 'warning',
          },
        ]}
      />
      <Box className="studio-panel studio-toolbar">
        <Input
          aria-label="Поиск мастера по качеству"
          placeholder="Поиск мастера по качеству"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          startDecorator={<SearchRoundedIcon />}
        />
        <Select
          aria-label="Месяц целей качества"
          value={month}
          onChange={(_, v) => v && setMonth(v)}
          startDecorator={<CalendarMonthRoundedIcon />}
        >
          {MONTHS.map((m, i) => (
            <Option key={i} value={i + 1}>
              {m}
            </Option>
          ))}
        </Select>
        <Select aria-label="Год целей качества" value={year} onChange={(_, v) => v && setYear(v)}>
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
            <Option key={y} value={y}>
              {y}
            </Option>
          ))}
        </Select>
      </Box>
      {loading ? (
        <Box className="studio-empty">
          <CircularProgress aria-label="Загрузка целей качества" />
        </Box>
      ) : !filtered.length ? (
        <Box className="studio-panel studio-empty">
          <Typography level="title-md">
            {search ? 'Мастера не найдены' : `На ${MONTHS[month - 1]} ${year} целей нет`}
          </Typography>
          {!search && (
            <Button sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>
              Добавить мастера
            </Button>
          )}
        </Box>
      ) : (
        <Box className="studio-quality-grid">
          <AnimatePresence initial={false}>
            {filtered.map((goal) => (
              <QualityGoalCard key={goal.id} goal={goal} onOpen={openGoal} />
            ))}
          </AnimatePresence>
        </Box>
      )}
      <Box className="studio-panel" sx={{ p: 2.5 }}>
        <Typography level="title-lg">Как рассчитывается результат</Typography>
        <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 1 }}>
          Соблюдение сроков, конверсия, переделки и длительность цикла. Целевые значения, веса показателей и
          оплата задаются в цели мастера.
        </Typography>
      </Box>
      <AddMasterModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        year={year}
        month={month}
        token={tokenEmployee}
        onPicked={onPicked}
      />
      <ModalQualityGoals
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        master={selectedMaster}
        initialGoal={selectedGoal}
        year={year}
        month={month}
        onSaved={onSaved}
        startInEdit={startEdit}
        canEdit={true}
      />
    </Workspace>
  );
}
